import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  CardInfoDetail,
  InstallmentOption,
  MerchantApproval,
} from "@/api/models/api-types";

// Checkout payload stored temporarily while approval modal is open
export interface CheckoutPayloadForApproval {
  installment: number;
  shippingAddress: {
    label: string;
    firstName: string;
    lastName: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  billingAddress: {
    label: string;
    firstName: string;
    lastName: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  card?: {
    cardHolderName: string;
    cardNumber: string;
    expireYear: string;
    expireMonth: string;
    cvc: string;
    saveCard?: boolean;
    alias?: string;
  };
  cardToken?: string;
  buyNow?: {
    offerId: string;
    quantity: number;
  };
}

export type AddressFormStatus = "closed" | "empty" | "invalid" | "valid";

// Address Interface (from old checkout-store, backend compatible)
export interface Address {
  _id: string; // Backend'den gelen ID
  label: string; // "Home", "Office", etc.
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone?: string;
  isDefault?: boolean;
}

// Payment Method Interface
export interface PaymentMethod {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  email: string;
  id: string;
  binNumber?: string; // İlk 8 hane - card info API için
  // Temporary: Store full card details for checkout (should be handled by payment gateway)
  fullCardNumber?: string; // Geçici: Tam kart numarası (production'da payment gateway handle etmeli)
  cvv?: string; // Geçici: CVV (production'da payment gateway handle etmeli)
}

interface CheckoutStore {
  // Address States
  selectedDeliveryAddressId: string | null;
  selectedBillingAddressId: string | null;
  useSameAddressForBilling: boolean;

  // Payment Method States
  selectedPaymentMethodId: string | null;
  paymentMethods: PaymentMethod[]; // Mock data - API entegrasyonu için hazır

  // Card Info & Installments States
  cardInfo: CardInfoDetail | null;
  installments: InstallmentOption[];
  selectedInstallmentNumber: number | null; // Seçili taksit sayısı (1 = tek çekim)

  // New Address Form States
  isAddingNewAddress: boolean; // Toggle between selection and form
  isAddingNewBillingAddress: boolean;
  isEditingDeliveryAddress: boolean;
  isEditingBillingAddress: boolean;
  deliveryAddressFormStatus: AddressFormStatus;
  billingAddressFormStatus: AddressFormStatus;
  addressFormFocusToken: number;

  // Modal States
  isAddressModalOpen: boolean;
  isDeleteAddressDialogOpen: boolean;
  editingAddressId: string | null;
  deletingAddressId: string | null;
  addressModalMode: "delivery" | "billing"; // Hangi address için modal açık

  // Payment Method Modal States
  isPaymentMethodModalOpen: boolean;
  isDeletePaymentMethodDialogOpen: boolean;
  editingPaymentMethodId: string | null;
  deletingPaymentMethodId: string | null;

  // 3DS Modal States
  is3DSModalOpen: boolean;
  threeDSHtmlContent: string | null;

  // Order Processing Screen States
  isProcessingScreenOpen: boolean;
  processingOrderGroupId: string | null;

  // Buy Now State (hemen al akışı - global cart store'u kirletmeden)
  buyNowOfferId: string | null;
  buyNowQuantity: number;

  // Kart Ödeme Modu
  cardMode: "new" | "saved"; // Yeni kart mı, saklı kart mı
  selectedSavedCardToken: string | null; // Seçili saklı kartın token'ı
  activeBinNumber: string | null; // Taksit hesabı için aktif BIN (saklı veya yeni kart)
  newCardData: {
    cardHolderName: string;
    cardNumber: string; // raw digits
    expireMonth: string; // MM
    expireYear: string; // YYYY
    cvc: string;
    saveCard: boolean;
    alias: string;
  } | null;

  // Shipping States (backend'den gelecek)
  shippingCosts: Record<string, number>; // vendorId -> shipping cost
  isLoadingShipping: boolean;

  // Merchant Approval Modal States
  isApprovalModalOpen: boolean;
  approvalMerchants: MerchantApproval[];
  pendingCheckoutPayload: CheckoutPayloadForApproval | null;

  // Actions
  setDeliveryAddress: (id: string | null) => void;
  setBillingAddress: (id: string | null) => void;
  toggleUseSameAddress: () => void;
  setPaymentMethod: (id: string | null) => void;

  // Payment Method Actions
  setPaymentMethods: (methods: PaymentMethod[]) => void;
  addPaymentMethod: (method: PaymentMethod) => void;
  updatePaymentMethod: (id: string, method: Partial<PaymentMethod>) => void;
  deletePaymentMethod: (id: string) => void;

  // Card Info & Installments Actions
  setCardInfo: (cardInfo: CardInfoDetail | null) => void;
  setInstallments: (installments: InstallmentOption[]) => void;
  setSelectedInstallment: (installmentNumber: number | null) => void;

  // Modal Actions
  openAddressModal: (mode: "delivery" | "billing", addressId?: string) => void;
  closeAddressModal: () => void;
  openDeleteAddressDialog: (addressId: string) => void;
  closeDeleteAddressDialog: () => void;

  // Payment Method Modal Actions
  openPaymentMethodModal: (paymentMethodId?: string) => void;
  closePaymentMethodModal: () => void;
  openDeletePaymentMethodDialog: (paymentMethodId: string) => void;
  closeDeletePaymentMethodDialog: () => void;

  // 3DS Modal Actions
  open3DSModal: (htmlContent: string) => void;
  close3DSModal: () => void;

  // Order Processing Screen Actions
  openProcessingScreen: (orderGroupId: string) => void;
  closeProcessingScreen: () => void;

  // New Address Form Actions
  setIsAddingNewAddress: (value: boolean) => void;
  setIsAddingNewBillingAddress: (value: boolean) => void;
  setIsEditingDeliveryAddress: (value: boolean) => void;
  setIsEditingBillingAddress: (value: boolean) => void;
  setDeliveryAddressFormStatus: (status: AddressFormStatus) => void;
  setBillingAddressFormStatus: (status: AddressFormStatus) => void;
  requestAddressFormFocus: () => void;

  // Buy Now Actions
  setBuyNowOfferId: (offerId: string | null) => void;
  setBuyNow: (offerId: string | null, quantity?: number) => void;

  // Kart Ödeme Modu Actions
  setCardMode: (mode: "new" | "saved") => void;
  setSelectedSavedCard: (token: string | null, binNumber?: string | null) => void;
  setActiveBinNumber: (bin: string | null) => void;
  setNewCardData: (data: CheckoutStore["newCardData"]) => void;

  // Shipping
  setShippingCosts: (costs: Record<string, number>) => void;
  setIsLoadingShipping: (loading: boolean) => void;

  // Merchant Approval Modal Actions
  openApprovalModal: (merchants: MerchantApproval[], payload: CheckoutPayloadForApproval) => void;
  closeApprovalModal: () => void;

  // Reset
  reset: () => void;
}

const initialState = {
  // Address
  selectedDeliveryAddressId: null,
  selectedBillingAddressId: null,
  useSameAddressForBilling: true,

  // Payment Method
  selectedPaymentMethodId: null,
  paymentMethods: [],

  // Card Info & Installments
  cardInfo: null,
  installments: [],
  selectedInstallmentNumber: null,

  // New Address Form
  isAddingNewAddress: false,
  isAddingNewBillingAddress: false,
  isEditingDeliveryAddress: false,
  isEditingBillingAddress: false,
  deliveryAddressFormStatus: "closed" as AddressFormStatus,
  billingAddressFormStatus: "closed" as AddressFormStatus,
  addressFormFocusToken: 0,

  // Modals
  isAddressModalOpen: false,
  isDeleteAddressDialogOpen: false,
  editingAddressId: null,
  deletingAddressId: null,
  addressModalMode: "delivery" as const,

  // Payment Method Modals
  isPaymentMethodModalOpen: false,
  isDeletePaymentMethodDialogOpen: false,
  editingPaymentMethodId: null,
  deletingPaymentMethodId: null,

  // 3DS Modal
  is3DSModalOpen: false,
  threeDSHtmlContent: null,

  // Order Processing Screen
  isProcessingScreenOpen: false,
  processingOrderGroupId: null,

  // Buy Now
  buyNowOfferId: null,
  buyNowQuantity: 1,

  // Kart Ödeme Modu
  cardMode: "new" as const,
  selectedSavedCardToken: null,
  activeBinNumber: null,
  newCardData: null,

  // Shipping
  shippingCosts: {},
  isLoadingShipping: false,

  // Merchant Approval Modal
  isApprovalModalOpen: false,
  approvalMerchants: [],
  pendingCheckoutPayload: null,
};

export const useCheckoutStore = create<CheckoutStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Address Actions
      setDeliveryAddress: (id) => {
        set({ selectedDeliveryAddressId: id });
        // Eğer same address kullanılıyorsa, billing'i de güncelle
        if (get().useSameAddressForBilling) {
          set({ selectedBillingAddressId: id });
        }
      },

      setBillingAddress: (id) => {
        set({ selectedBillingAddressId: id });
      },

      toggleUseSameAddress: () => {
        const newValue = !get().useSameAddressForBilling;
        set({ useSameAddressForBilling: newValue });

        // Eğer same address aktif edilirse, billing'i delivery ile senkronize et
        if (newValue) {
          set({ selectedBillingAddressId: get().selectedDeliveryAddressId });
        }
      },

      setPaymentMethod: (id) => {
        set({ selectedPaymentMethodId: id });
      },

      // Payment Method Actions
      setPaymentMethods: (methods) => {
        set({ paymentMethods: methods });
        // Eğer seçili kart yoksa ve method'lar varsa, ilkini seç
        if (!get().selectedPaymentMethodId && methods.length > 0) {
          set({ selectedPaymentMethodId: methods[0].id });
        }
      },

      addPaymentMethod: (method) => {
        const currentMethods = get().paymentMethods;
        set({
          paymentMethods: [...currentMethods, method],
          selectedPaymentMethodId: method.id, // Yeni eklenen kartı otomatik seç
        });
      },

      updatePaymentMethod: (id, updatedData) => {
        const currentMethods = get().paymentMethods;
        set({
          paymentMethods: currentMethods.map((method) =>
            method.id === id ? { ...method, ...updatedData } : method
          ),
        });
      },

      deletePaymentMethod: (id) => {
        const currentMethods = get().paymentMethods;
        const remainingMethods = currentMethods.filter(
          (method) => method.id !== id
        );
        set({ paymentMethods: remainingMethods });

        // Eğer silinen kart seçiliyse, başka bir kart seç veya null yap
        if (get().selectedPaymentMethodId === id) {
          if (remainingMethods.length > 0) {
            set({ selectedPaymentMethodId: remainingMethods[0].id });
          } else {
            set({ selectedPaymentMethodId: null });
            // Kart bilgilerini de temizle
            set({ cardInfo: null, installments: [], selectedInstallmentNumber: null });
          }
        }
      },

      // Card Info & Installments Actions
      setCardInfo: (cardInfo) => {
        set({ cardInfo });
      },

      setInstallments: (installments) => {
        set({ installments });
        // Eğer taksit seçili değilse, ilk taksiti (tek çekim) seç
        if (get().selectedInstallmentNumber === null && installments.length > 0) {
          set({ selectedInstallmentNumber: installments[0].installmentNumber });
        }
      },

      setSelectedInstallment: (installmentNumber) => {
        set({ selectedInstallmentNumber: installmentNumber });
      },

      // Modal Actions
      openAddressModal: (mode, addressId) => {
        set({
          isAddressModalOpen: true,
          addressModalMode: mode,
          editingAddressId: addressId || null,
        });
      },

      closeAddressModal: () => {
        set({
          isAddressModalOpen: false,
          editingAddressId: null,
        });
      },

      openDeleteAddressDialog: (addressId) => {
        set({
          isDeleteAddressDialogOpen: true,
          deletingAddressId: addressId,
        });
      },

      closeDeleteAddressDialog: () => {
        set({
          isDeleteAddressDialogOpen: false,
          deletingAddressId: null,
        });
      },

      // Payment Method Modal Actions
      openPaymentMethodModal: (paymentMethodId) => {
        set({
          isPaymentMethodModalOpen: true,
          editingPaymentMethodId: paymentMethodId || null,
        });
      },

      closePaymentMethodModal: () => {
        set({
          isPaymentMethodModalOpen: false,
          editingPaymentMethodId: null,
        });
      },

      openDeletePaymentMethodDialog: (paymentMethodId) => {
        set({
          isDeletePaymentMethodDialogOpen: true,
          deletingPaymentMethodId: paymentMethodId,
        });
      },

      closeDeletePaymentMethodDialog: () => {
        set({
          isDeletePaymentMethodDialogOpen: false,
          deletingPaymentMethodId: null,
        });
      },

      // 3DS Modal Actions
      open3DSModal: (htmlContent) => {
        set({
          is3DSModalOpen: true,
          threeDSHtmlContent: htmlContent,
        });
      },

      close3DSModal: () => {
        set({
          is3DSModalOpen: false,
          threeDSHtmlContent: null,
        });
      },

      // Order Processing Screen Actions
      openProcessingScreen: (orderGroupId) => {
        set({
          isProcessingScreenOpen: true,
          processingOrderGroupId: orderGroupId,
        });
      },

      closeProcessingScreen: () => {
        set({
          isProcessingScreenOpen: false,
          processingOrderGroupId: null,
        });
      },

      // New Address Form Actions
      setIsAddingNewAddress: (value) => {
        set({
          isAddingNewAddress: value,
          ...(value ? { isEditingDeliveryAddress: false } : {}),
          ...(!value ? { deliveryAddressFormStatus: "closed" as AddressFormStatus } : {}),
        });
      },

      setIsAddingNewBillingAddress: (value) => {
        set({
          isAddingNewBillingAddress: value,
          ...(value ? { isEditingBillingAddress: false } : {}),
          ...(!value ? { billingAddressFormStatus: "closed" as AddressFormStatus } : {}),
        });
      },

      setIsEditingDeliveryAddress: (value) => {
        set({
          isEditingDeliveryAddress: value,
          ...(value ? { isAddingNewAddress: false } : {}),
          ...(!value ? { deliveryAddressFormStatus: "closed" as AddressFormStatus } : {}),
        });
      },

      setIsEditingBillingAddress: (value) => {
        set({
          isEditingBillingAddress: value,
          ...(value ? { isAddingNewBillingAddress: false } : {}),
          ...(!value ? { billingAddressFormStatus: "closed" as AddressFormStatus } : {}),
        });
      },

      setDeliveryAddressFormStatus: (status) => {
        if (get().deliveryAddressFormStatus === status) return;
        set({ deliveryAddressFormStatus: status });
      },

      setBillingAddressFormStatus: (status) => {
        if (get().billingAddressFormStatus === status) return;
        set({ billingAddressFormStatus: status });
      },

      requestAddressFormFocus: () => {
        set({ addressFormFocusToken: get().addressFormFocusToken + 1 });
      },

      // Buy Now Actions
      setBuyNowOfferId: (offerId) => {
        set({ buyNowOfferId: offerId });
      },
      setBuyNow: (offerId, quantity = 1) => {
        set({ buyNowOfferId: offerId, buyNowQuantity: quantity });
      },

      // Kart Ödeme Modu Actions
      setCardMode: (mode) => {
        set({ cardMode: mode });
      },
      setSelectedSavedCard: (token, binNumber) => {
        set({
          selectedSavedCardToken: token,
          activeBinNumber: binNumber ?? null,
          cardMode: token ? "saved" : "new",
        });
      },
      setActiveBinNumber: (bin) => {
        set({ activeBinNumber: bin });
      },
      setNewCardData: (data) => {
        set({ newCardData: data, cardMode: "new" });
      },

      // Shipping
      setShippingCosts: (costs) => {
        set({ shippingCosts: costs });
      },

      setIsLoadingShipping: (loading) => {
        set({ isLoadingShipping: loading });
      },

      // Merchant Approval Modal Actions
      openApprovalModal: (merchants, payload) => {
        set({
          isApprovalModalOpen: true,
          approvalMerchants: merchants,
          pendingCheckoutPayload: payload,
        });
      },

      closeApprovalModal: () => {
        set({
          isApprovalModalOpen: false,
          approvalMerchants: [],
          pendingCheckoutPayload: null,
        });
      },

      // Reset
      reset: () => {
        set(initialState);
      },
    }),
    {
      name: "checkout-storage",
      partialize: (state) => ({
        // Persist only address and payment method selections
        selectedDeliveryAddressId: state.selectedDeliveryAddressId,
        selectedBillingAddressId: state.selectedBillingAddressId,
        useSameAddressForBilling: state.useSameAddressForBilling,
        selectedPaymentMethodId: state.selectedPaymentMethodId,
      }),
    }
  )
);
